import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Lock, Mail, Settings, CreditCard, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { stripeService } from '../../services/stripeService';
import { supabaseClient } from '../../config/supabase';
import toast from 'react-hot-toast';

export default function UserProfile() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plan, quizGenerationsLeft, resetsLeft } = useSubscriptionStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [newEmail, setNewEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      toast.loading('Otwieranie panelu zarządzania...');
      
      const { url } = await stripeService.createPortalSession(user.id);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Nie otrzymano URL do panelu zarządzania');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      toast.error('Nie udało się otworzyć panelu zarządzania. Spróbuj ponownie później.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }

    try {
      setIsLoading(true);
      
      const { error } = await supabaseClient.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (error) throw error;
      
      toast.success('Hasło zostało zmienione. Sprawdź email z potwierdzeniem.');
      setIsChangingPassword(false);
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Nie udało się zmienić hasła');
    } finally {
      setIsLoading(false);
    }
  };

  // Funkcja walidująca email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const updateEmail = async () => {
    if (!newEmail || !validateEmail(newEmail)) {
      toast.error('Podaj poprawny adres e-mail');
      return;
    }

    setIsUpdating(true);
    
    try {
      const { error } = await supabaseClient.auth.updateUser({
        email: newEmail
      });

      if (error) {
        throw error;
      }
      
      toast.success('Wysłano link potwierdzający na nowy adres e-mail. Sprawdź swoją skrzynkę.');
      setNewEmail('');
    } catch (error: any) {
      console.error('Błąd podczas aktualizacji e-mail:', error);
      toast.error(error.message || 'Nie udało się zaktualizować adresu e-mail');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sekcja Generacji */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Generacje</h2>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                plan === 'premium' 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                Plan {plan === 'premium' ? 'Premium' : 'Darmowy'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Wygenerowane analizy
              </h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">{
                plan === 'premium' 
                  ? `${5 - quizGenerationsLeft}/5`
                  : (localStorage.getItem(`analysis_${user?.id}`) ? '1/1' : '0/1')
              }
              </p>
              {plan !== 'premium' && (
                <p className="text-sm text-purple-600">
                  Odblokuj 5 generacji w Premium
                </p>
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Pozostałe resety
              </h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {resetsLeft}/{plan === 'premium' ? '5' : '0'}
              </p>
              {plan !== 'premium' && (
                <p className="text-sm text-purple-600">
                  Dostępne tylko w Premium
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sekcja Subskrypcji */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Status subskrypcji
          </h2>

          {plan === 'premium' ? (
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Plan Premium</h3>
                  <p className="text-gray-600">
                    Pełen dostęp do wszystkich funkcji
                    <br />
                    <span className="text-sm text-gray-500">
                      Możesz anulować subskrypcję w dowolnym momencie
                    </span>
                  </p>
                </div>
              </div>

              <button
                onClick={handleManageSubscription}
                disabled={isLoading}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Ładowanie...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Zarządzaj subskrypcją
                  </>
                )}
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Plan Darmowy</h3>
                  <p className="text-gray-600">Podstawowe funkcje platformy</p>
                </div>
              </div>

              <button
                onClick={() => navigate('/plany')}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                Przejdź do Premium
              </button>
            </div>
          )}
        </div>

        {/* Sekcja Kontaktu */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Ustawienia konta</h3>
              <p className="text-gray-600">Zarządzaj swoim kontem</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500"
              />
            </div>

            {isChangingPassword ? (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nowe hasło
                  </label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    minLength={8}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Potwierdź nowe hasło
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    minLength={8}
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Zapisywanie...' : 'Zapisz nowe hasło'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsChangingPassword(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Lock className="w-4 h-4" />
                Zmień hasło
              </button>
            )}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Kontakt</h3>
              <p className="text-gray-600">Pomoc i wsparcie</p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-gray-600">
              Masz pytania dotyczące swojego konta lub danych osobowych?
              Skontaktuj się z nami:
            </p>
            <a
              href="mailto:polokontaktowy@gmail.com"
              className="inline-block text-purple-600 hover:text-purple-700"
            >
              polokontaktowy@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
